import {
  memo,
  forwardRef,
  useEffect,
  useState,
  useCallback,
  useRef,
} from 'react';

const DEFAULT_KRPANO_SCRIPT_SRC = `${import.meta.env.BASE_URL}file_360/tour.js`;
const SCENE_BLEND = 'BLEND(0.5)';

const KrpanoViewer = forwardRef(
  ({ startScene, sceneFolder, scriptSrc, onReady, onClick }, ref) => {
    const resolvedScriptSrc = scriptSrc ?? DEFAULT_KRPANO_SCRIPT_SRC;
    const [krpanoReady, setKrpanoReady] = useState(false);
    const onReadyRef = useRef(onReady);
    const containerIdRef = useRef(
      `krpanoContainer-${Math.random().toString(36).slice(2)}`
    );
    useEffect(() => {
      onReadyRef.current = onReady;
    }, [onReady]);

    const initKrpano = useCallback(() => {
      if (!window.embedpano) {
        console.warn('[KrpanoViewer] embedpano is not available yet');
        return;
      }

      const container = document.getElementById(containerIdRef.current);
      if (container) container.innerHTML = '';

      const [sceneName, queryString = ''] = startScene.split('&');
      const params = new URLSearchParams(queryString);
      const sceneVars = Object.fromEntries(params.entries());
      const xmlPath = `${sceneFolder}/tour.xml`;

      window.embedpano({
        swf: null,
        xml: xmlPath,
        vars: {
          startscene: sceneName,
          ...sceneVars,
        },
        target: containerIdRef.current,
        passQueryParameters: 'startscene,startlookat,littleplanetintro',
        html5: 'only',
        onready: (krpanoInterface) => {
          if (!krpanoInterface) return;

          if (ref && typeof ref !== 'function') {
            ref.current = krpanoInterface;
          }
          setKrpanoReady(true);
          onReadyRef.current?.(krpanoInterface);

          // krpano's iframe grabs focus on init — redirect to focus-anchor so
          // the next Tab press lands on the skip-navigation link, not mid-page.
          requestAnimationFrame(() => {
            const container = document.getElementById(containerIdRef.current);
            if (container) {
              const iframe = container.querySelector('iframe');
              if (iframe) iframe.setAttribute('tabindex', '-1');
            }
            document.getElementById('focus-anchor')?.focus({ preventScroll: true });
          });
        },
      });
    }, [sceneFolder, startScene, ref]);

    // Load krpano script and initialize viewer
    useEffect(() => {
      let cancelled = false;

      const tryInit = () => {
        if (cancelled) return;
        if (!window.embedpano) return;
        initKrpano();
      };

      const attachAndLoadScript = () => {
        // Use src as a unique key so multiple different tour.js files can coexist
        const safeKey = resolvedScriptSrc.replace(/[^a-z0-9]/gi, '_');
        const existing = document.querySelector(
          `script[data-krpano-src-key="${safeKey}"]`
        );
        if (existing) {
          if (window.embedpano) {
            tryInit();
          } else {
            existing.addEventListener('load', tryInit, { once: true });
            existing.addEventListener(
              'error',
              () => {
                console.error(
                  '[KrpanoViewer] Failed to load krpano script:',
                  resolvedScriptSrc
                );
              },
              { once: true }
            );
            queueMicrotask(() => {
              if (!cancelled && window.embedpano) tryInit();
            });
          }
          return;
        }

        const script = document.createElement('script');
        script.src = resolvedScriptSrc;
        script.async = true;
        script.dataset.krpanoTourJs = '1';
        script.dataset.krpanoSrcKey = safeKey;
        script.onload = () => {
          window.krpanoScriptLoaded = true;
          tryInit();
        };
        script.onerror = () => {
          console.error(
            '[KrpanoViewer] Script request failed:',
            resolvedScriptSrc
          );
        };
        document.body.appendChild(script);
      };

      if (window.embedpano) {
        tryInit();
      } else {
        attachAndLoadScript();
      }

      return () => {
        cancelled = true;
        window.removepano?.(containerIdRef.current);
        if (ref && typeof ref !== 'function') {
          ref.current = null;
        }
      };
    }, [initKrpano, ref]);

    // Load a new scene after krpano is ready
    useEffect(() => {
      if (typeof ref === 'function') return;
      if ((!krpanoReady || !startScene) && !ref?.current) return;

      const [sceneName, queryString] = startScene.split('&');
      const params = new URLSearchParams(queryString);

      const sceneSetCalls = Array.from(params.entries())
        .map(([key, value]) => `set(scene[${sceneName}].${key}, "${value}");`)
        .join(' ');

      const currentScene = ref?.current?.get?.('xml.scene');
      const shouldLoadScene = currentScene !== sceneName;
      if (sceneSetCalls || shouldLoadScene) {
        ref?.current?.call(
          `${sceneSetCalls} ${shouldLoadScene ? `loadscene(${sceneName}, null, MERGE, ${SCENE_BLEND});` : ''}`
        );
      }
    }, [startScene, krpanoReady, ref]);

    return (
      <div
        id={containerIdRef.current}
        style={{ width: '100%', height: '100%' }}
        onClick={onClick}
      />
    );
  }
);

KrpanoViewer.displayName = 'KrpanoViewer';

export default memo(KrpanoViewer);
